import {
  ConnectionCatalogDocument,
  type ConnectionCatalogDocument as ConnectionCatalogDocumentType,
  ConnectionPersistenceError,
  ConnectionRegistrationStore,
  ConnectionTargetStore,
  EMPTY_CONNECTION_CATALOG_DOCUMENT,
  EnvironmentCacheStore,
  registerConnectionInCatalog,
  removeCatalogValue,
  removeConnectionFromCatalog,
  replaceCatalogValue,
} from "@t3tools/client-runtime/platform";
import { TokenStore } from "@t3tools/client-runtime/authorization";
import {
  ConnectionTransientError,
  CredentialRenewal,
  CredentialStore,
  ProfileStore,
} from "@t3tools/client-runtime/connection";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Ref from "effect/Ref";
import * as Schema from "effect/Schema";
import * as Semaphore from "effect/Semaphore";

import { currentAccountId, onAccountLifetimeClose } from "../zerops/accountLifetime";
import { endMateSession, endWhenAccountLeft } from "../zerops/keptSessions";

const jsonCatalog = Schema.fromJsonString(ConnectionCatalogDocument);
const catalogError = (cause: unknown) =>
  new ConnectionTransientError({ reason: "remote-unavailable", detail: String(cause) });
const decodeCatalog = (raw: string) =>
  Schema.decodeUnknownEffect(jsonCatalog)(raw).pipe(Effect.mapError(catalogError));
const encodeCatalog = (value: ConnectionCatalogDocumentType) =>
  Schema.encodeEffect(jsonCatalog)(value).pipe(Effect.mapError(catalogError));
const persistenceError = (
  operation: "list-targets" | "register-connection" | "remove-connection",
  cause: unknown,
) => new ConnectionPersistenceError({ operation, message: String(cause) });

export interface CatalogBackend {
  readonly read: Effect.Effect<string | null, ConnectionTransientError>;
  readonly write: (raw: string) => Effect.Effect<void, ConnectionTransientError>;
  readonly quarantine?: (raw: string) => Effect.Effect<void, ConnectionTransientError>;
}

/** Credentials and server snapshots belong to this verified login only. The
 * old IndexedDB and desktop catalogs have no account owner and are never read. */
export function makeCatalogBackend(): CatalogBackend {
  let value: string | null = null;
  return {
    read: Effect.sync(() => value),
    write: (raw) =>
      Effect.sync(() => {
        value = raw;
      }),
  };
}

interface CatalogStore {
  readonly read: Effect.Effect<ConnectionCatalogDocumentType, ConnectionTransientError>;
  readonly update: (
    transform: (catalog: ConnectionCatalogDocumentType) => ConnectionCatalogDocumentType,
  ) => Effect.Effect<void, ConnectionTransientError>;
}

export const makeCatalogStore = Effect.fn("web.connectionStorage.makeCatalogStore")(function* (
  backend: CatalogBackend,
) {
  const state = yield* Ref.make<Option.Option<ConnectionCatalogDocumentType>>(Option.none());
  const lock = yield* Semaphore.make(1);

  const loadUnlocked = Effect.fn("web.connectionStorage.loadCatalog")(function* () {
    const cached = yield* Ref.get(state);
    if (Option.isSome(cached)) {
      return cached.value;
    }
    const raw = yield* backend.read;
    let catalog = EMPTY_CONNECTION_CATALOG_DOCUMENT;
    if (raw !== null && raw.trim() !== "") {
      catalog = yield* decodeCatalog(raw).pipe(
        Effect.catch((error) =>
          Effect.gen(function* () {
            yield* Effect.logWarning("Discarding a corrupt web connection catalog.", {
              error: error.message,
            });
            if (backend.quarantine !== undefined) {
              yield* backend.quarantine(raw).pipe(
                Effect.catch((cause) =>
                  Effect.logWarning("Could not quarantine the corrupt web connection catalog.", {
                    error: cause.message,
                  }),
                ),
              );
            }
            const encoded = yield* encodeCatalog(EMPTY_CONNECTION_CATALOG_DOCUMENT);
            yield* backend.write(encoded).pipe(
              Effect.catch((cause) =>
                Effect.logWarning("Could not persist the recovered web connection catalog.", {
                  error: cause.message,
                }),
              ),
            );
            return EMPTY_CONNECTION_CATALOG_DOCUMENT;
          }),
        ),
      );
    }
    yield* Ref.set(state, Option.some(catalog));
    return catalog;
  });

  const read = lock.withPermits(1)(loadUnlocked());
  const update: CatalogStore["update"] = Effect.fn("web.connectionStorage.updateCatalog")(
    function* (transform) {
      yield* lock.withPermits(1)(
        Effect.gen(function* () {
          const next = transform(yield* loadUnlocked());
          yield* backend.write(yield* encodeCatalog(next));
          yield* Ref.set(state, Option.some(next));
        }),
      );
    },
  );

  return { read, update } satisfies CatalogStore;
});

/**
 * The logouts an account's close sends: one per stored bearer whose session may still be live. A
 * session past its deadline has already ended on its Mate; presenting it would only be refused.
 * The sessions kept for later loads are ended by their own closer (`zerops/keptSessions.ts`).
 */
export function accountCloseLogouts(
  document: ConnectionCatalogDocumentType,
  nowEpochMs: number,
): ReadonlyArray<{ readonly url: string; readonly token: string }> {
  return document.profiles.flatMap((profile) => {
    if (profile._tag !== "BearerConnectionProfile") return [];
    const credential = document.credentials.find(
      (entry) => entry.connectionId === profile.connectionId,
    )?.credential;
    if (!credential || CredentialRenewal.credentialExpired(credential, nowEpochMs)) return [];
    return [
      {
        url: `${profile.httpBaseUrl.replace(/\/+$/, "")}/api/auth/logout`,
        token: credential.token,
      },
    ];
  });
}

export const connectionStorageLayer = Layer.effectContext(
  Effect.gen(function* () {
    if (currentAccountId() === null) {
      return yield* new ConnectionTransientError({
        reason: "remote-unavailable",
        detail: "Sign in to Zerops first.",
      });
    }
    let currentCatalog = EMPTY_CONNECTION_CATALOG_DOCUMENT;
    const catalog = yield* makeCatalogStore({
      read: Effect.succeed(null),
      write: (raw) =>
        decodeCatalog(raw).pipe(
          Effect.tap((document) =>
            Effect.sync(() => {
              currentCatalog = document;
            }),
          ),
          Effect.asVoid,
        ),
    });
    const unregisterLogout = onAccountLifetimeClose(() => {
      // The credentials are captured before the Effect runtime is disposed; none can touch a
      // subsequent login's catalog. They end once no other tab holds the account open.
      const logouts = accountCloseLogouts(currentCatalog, Date.now());
      return endWhenAccountLeft(() => {
        for (const logout of logouts) endMateSession(logout);
      });
    });
    yield* Effect.addFinalizer(() => Effect.sync(unregisterLogout));
    const targetStore = ConnectionTargetStore.of({
      list: catalog.read.pipe(
        Effect.map((document) => document.targets),
        Effect.mapError((cause) => persistenceError("list-targets", cause)),
      ),
    });
    const registrationStore = ConnectionRegistrationStore.of({
      register: (registration) =>
        catalog
          .update((document) => registerConnectionInCatalog(document, registration))
          .pipe(Effect.mapError((cause) => persistenceError("register-connection", cause))),
      remove: (target) =>
        catalog
          .update((document) => removeConnectionFromCatalog(document, target))
          .pipe(Effect.mapError((cause) => persistenceError("remove-connection", cause))),
    });
    const profileStore = ProfileStore.make({
      get: (connectionId) =>
        catalog.read.pipe(
          Effect.map((document) =>
            Option.fromUndefinedOr(
              document.profiles.find((profile) => profile.connectionId === connectionId),
            ),
          ),
        ),
      put: (profile) =>
        catalog.update((document) => ({
          ...document,
          profiles: replaceCatalogValue(document.profiles, (value) => value.connectionId, profile),
        })),
      remove: (connectionId) =>
        catalog.update((document) => ({
          ...document,
          profiles: removeCatalogValue(
            document.profiles,
            (value) => value.connectionId,
            connectionId,
          ),
        })),
    });
    const credentialStore = CredentialStore.make({
      get: (connectionId) =>
        catalog.read.pipe(
          Effect.map((document) =>
            Option.fromUndefinedOr(
              document.credentials.find((entry) => entry.connectionId === connectionId)?.credential,
            ),
          ),
        ),
      put: (connectionId, credential) =>
        catalog.update((document) => ({
          ...document,
          credentials: replaceCatalogValue(document.credentials, (value) => value.connectionId, {
            connectionId,
            credential,
          }),
        })),
      remove: (connectionId) =>
        catalog.update((document) => ({
          ...document,
          credentials: removeCatalogValue(
            document.credentials,
            (value) => value.connectionId,
            connectionId,
          ),
        })),
    });
    const remoteTokenStore = TokenStore.make({
      get: (environmentId) =>
        catalog.read.pipe(
          Effect.map((document) =>
            Option.fromUndefinedOr(
              document.remoteDpopTokens.find((token) => token.environmentId === environmentId),
            ),
          ),
        ),
      put: (token) =>
        catalog.update((document) => ({
          ...document,
          remoteDpopTokens: replaceCatalogValue(
            document.remoteDpopTokens,
            (value) => value.environmentId,
            token,
          ),
        })),
      remove: (environmentId) =>
        catalog.update((document) => ({
          ...document,
          remoteDpopTokens: removeCatalogValue(
            document.remoteDpopTokens,
            (value) => value.environmentId,
            environmentId,
          ),
        })),
    });
    const cacheStore = EnvironmentCacheStore.of({
      loadShell: () => Effect.succeed(Option.none()),
      saveShell: () => Effect.void,
      loadThread: () => Effect.succeed(Option.none()),
      saveThread: () => Effect.void,
      removeThread: () => Effect.void,
      loadServerConfig: () => Effect.succeed(Option.none()),
      saveServerConfig: () => Effect.void,
      loadVcsRefs: () => Effect.succeed(Option.none()),
      saveVcsRefs: () => Effect.void,
      removeVcsRefs: () => Effect.void,
      clearVcsRefs: () => Effect.void,
      clear: () => Effect.void,
    });

    return Context.make(ConnectionTargetStore, targetStore).pipe(
      Context.add(ConnectionRegistrationStore, registrationStore),
      Context.add(ProfileStore.ConnectionProfileStore, profileStore),
      Context.add(CredentialStore.ConnectionCredentialStore, credentialStore),
      Context.add(TokenStore.RemoteDpopAccessTokenStore, remoteTokenStore),
      Context.add(EnvironmentCacheStore, cacheStore),
    );
  }),
);
